import React, { useCallback, useMemo } from 'react';
import { Table } from '@cognitiv/cyprus-ui';
import { useAppDispatch } from 'ducks/hooks';
import { setModal } from 'ducks/modals/slices';
import { setParameter } from 'ducks/parameter/slices';
import { ParametersTableItem, ProjectParameter } from 'ducks/types/common-types';
import { ParametersProps } from 'ducks/types/prop-types';
import { ProjectParameters } from 'ducks/types/store-types';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { input_headers } from 'modals/constants';
import {
  ActionCell,
  DefaultCell,
  DescriptionCell,
  MethodCell,
  ParameterNameCell,
  RequiredCell,
} from 'modals/parameters/components/Cells';
import { useConfiguredRows } from 'modals/parameters/components/hooks/useConfiguredRows';
import { ManageParameter } from 'modals/parameters/ManageParameter';
import { handleError } from 'utils/errors';
import { getNewParameterRow } from 'utils/modal/helpers';

export const Parameters = ({ setParameters, parameters, force_update }: ParametersProps) => {
  const dispatch = useAppDispatch();
  const { is_dark_theme } = useSwitchTheme();
  const { getConfiguredRows } = useConfiguredRows();

  const handleParameter = useCallback(
    (row: ProjectParameter) => {
      setParameters(row, true);
    },
    [setParameters],
  );

  const handleRequired = useCallback(
    (is_required: boolean, row: ProjectParameter) => {
      const new_row = getNewParameterRow('is_required', is_required);
      setParameters({ ...row, ...new_row, uuid: row.uuid });
    },
    [setParameters],
  );

  const editParameter = useCallback(
    (row: ProjectParameter) => {
      dispatch(setParameter(row));
      dispatch(setModal({ manage_parameter: true }));
    },
    [dispatch],
  );

  const deleteParameter = useCallback(
    (row: ProjectParameter) => {
      const new_row = getNewParameterRow('delete', true);
      setParameters({ ...row, ...new_row, uuid: row.uuid });
    },
    [setParameters],
  );

  const handleParameterName = useCallback(
    (value: string, row: ProjectParameter) => {
      const new_row = getNewParameterRow('parameter_name', value);
      setParameters({ ...row, ...new_row, uuid: row.uuid });
    },
    [setParameters],
  );

  const handleParameterDescription = useCallback(
    (value: string, row: ProjectParameter) => {
      const new_row = getNewParameterRow('description', value);
      setParameters({ ...row, ...new_row, uuid: row.uuid });
    },
    [setParameters],
  );

  const handleParameterDefault = useCallback(
    (value: string, row: ProjectParameter) => {
      const new_row = getNewParameterRow('parameter_value', value);
      setParameters({ ...row, ...new_row, uuid: row.uuid });
    },
    [setParameters],
  );

  const configureRows = useCallback(
    (rows: ProjectParameters) => {
      const configured_rows = getConfiguredRows(rows);
      return configured_rows;
    },
    [getConfiguredRows],
  );

  const handleActions = useCallback(
    async (item: ParametersTableItem) => {
      try {
        switch (item.trigger) {
          case 'edit':
            editParameter(item.row);
            break;
          case 'delete':
            deleteParameter(item.row);
            break;
          default:
            throw new Error('Job Definition failed to perform action');
        }
      } catch (err) {
        const error = err as unknown as Error;
        handleError({ error: error.message, ...error });
      }
    },
    [deleteParameter, editParameter],
  );

  const memoizedTableRows = useMemo(() => configureRows(parameters), [configureRows, parameters]);
  return (
    <>
      <Table
        settings={{ row_height: 32, header_row_height: 32 }}
        rows={memoizedTableRows}
        headers={input_headers}
        key={`params_${is_dark_theme}_${force_update}`}
      >
        <RequiredCell handleRequired={handleRequired} />
        <ParameterNameCell handleParameterName={handleParameterName} />
        <DefaultCell handleParameterDefault={handleParameterDefault} />
        <DescriptionCell handleParameterDescription={handleParameterDescription} />
        <MethodCell editParameter={editParameter} />
        <ActionCell handleActions={handleActions} />
      </Table>
      <ManageParameter onParameterChange={handleParameter} />
    </>
  );
};
